// @file Utils for processing posts on Surface and Slideshow

import device from '@@/bits/device'
import { getHostnameFromUrl, transformUrl } from '@@/bits/location'
import { mathifyElement } from '@@/bits/math'
import { PADLET_DOMAIN_REGEX } from '@@/bits/regex'
import {
  isBreakoutLinkUrlFormat,
  isInternalUrl,
  isProfileUrlFormat,
  isSlideshowUrlFormat,
  isSubmissionRequestUrlFormat,
  isWallOrWallWishUrlFormat,
  NATIVE_HOST,
} from '@@/bits/url'
import { BRIDGED_LINK_TYPE_ATTRIBUTE } from '@@/native_bridge/anchor'
import type { NativeAppOpenLink } from '@@/pinia/native_app'
import { MENTION_ELEMENT_TAG_NAME } from '@padlet/universal-post-editor'

const setRedirectUrlsForExternalLinks = (element: HTMLElement): void => {
  const links = element.querySelectorAll('a[href^="h"]')
  links.forEach((el) => {
    el.setAttribute('target', '_blank')
    el.setAttribute('rel', 'noopener nofollow ugc')
    const href = el.getAttribute('href')?.trim() ?? ''
    if (!isInternalUrl(href)) {
      el.setAttribute('href', transformToSafeRedirectUrl(href))
    }
  })
}

const transformToSafeRedirectUrl = (url: string): string => {
  return transformUrl(`https://${NATIVE_HOST}`, {
    path: 'redirect',
    search: {
      url,
    },
  })
}

const setExternalLinksForNativeBridge = (element: HTMLElement): void => {
  const links = element.querySelectorAll('a[href^="h"]')
  links.forEach((link) => {
    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noopener nofollow ugc')
    link.setAttribute(BRIDGED_LINK_TYPE_ATTRIBUTE, 'external')
  })
}

const isMentionLink = (el: Element): boolean => {
  return el.tagName === 'A' && el.parentElement?.tagName === MENTION_ELEMENT_TAG_NAME.toUpperCase()
}

// check to see if the url links to a padlet and set data-bridged-link to internal
const makeBodyPadletLinksInternalForNativeBridge = (element: HTMLElement): void => {
  if (!device.app) return

  const bodyAnchorTags = element.querySelectorAll('a[href^=h]')
  for (const anchorTag of bodyAnchorTags) {
    const hrefUrl = anchorTag.getAttribute('href')
    if (hrefUrl == null) continue

    const hrefHost = getHostnameFromUrl(hrefUrl)
    if (!PADLET_DOMAIN_REGEX.test(hrefHost)) return

    if (isMentionLink(anchorTag)) {
      anchorTag.setAttribute(BRIDGED_LINK_TYPE_ATTRIBUTE, 'profile')
    } else if (isSlideshowUrlFormat(hrefUrl)) {
      anchorTag.setAttribute(BRIDGED_LINK_TYPE_ATTRIBUTE, 'slideshow')
    } else if (isWallOrWallWishUrlFormat(hrefUrl)) {
      anchorTag.setAttribute(BRIDGED_LINK_TYPE_ATTRIBUTE, 'padlet')
    }
  }
}

/**
 * Generates the payload for internal links to be opened in the native app.
 *
 * @param link - The URL of the internal link
 * @returns An object containing the link type, content category, and subcategory (if applicable)
 *
 * NOTE: When adding new cases for handling URLs, the order of checks should be based on the specificity of the path.
 * If a new link type is a subset of an existing regex check, it must be placed after the corresponding conditional.
 * This ensures that more specific cases are handled before more general ones.
 */
const getNativeLinkPayloadForInternalLink = (link: string): Omit<NativeAppOpenLink, 'url'> => {
  if (isSlideshowUrlFormat(link)) {
    return { linkType: 'internal', contentCategory: 'page', contentSubcategory: 'slideshow' }
  }
  if (isWallOrWallWishUrlFormat(link)) {
    return { linkType: 'internal', contentCategory: 'page', contentSubcategory: 'padlet' }
  }
  if (isProfileUrlFormat(link)) {
    return { linkType: 'internal', contentCategory: 'page', contentSubcategory: 'profile' }
  }
  if (isSubmissionRequestUrlFormat(link)) {
    return { linkType: 'internal', contentCategory: 'page', contentSubcategory: 'submission-request' }
  }
  if (isBreakoutLinkUrlFormat(link)) {
    return { linkType: 'internal', contentCategory: 'page', contentSubcategory: 'breakout-link' }
  }
  // could be dashboard internal links or other subcategories that we don't handle in the native app yet
  return { linkType: 'internal', contentCategory: 'page' }
}

function mathifyPostBody(element: HTMLElement): void {
  const mathEls = element.querySelectorAll('var')
  if (mathEls.length > 0) {
    mathEls.forEach((el) => {
      if (el.firstElementChild == null || el.firstElementChild.className !== 'katex') {
        void mathifyElement(el)
      }
    })
  }
}

const MATCH_ANY_WORD_REGEX = /\w+/g
function getNumberOfWords(text?: string | null): number {
  if (text == null || text.length === 0) return 0
  const matches = text.match(MATCH_ANY_WORD_REGEX)
  return matches != null ? matches.length : 0
}

export {
  getNativeLinkPayloadForInternalLink,
  getNumberOfWords,
  isMentionLink,
  makeBodyPadletLinksInternalForNativeBridge,
  mathifyPostBody,
  setExternalLinksForNativeBridge,
  setRedirectUrlsForExternalLinks,
  transformToSafeRedirectUrl,
}
